/**
 * Date utility functions to avoid timezone issues
 * 
 * Problem: When creating new Date("2025-01-20"), JavaScript interprets it as UTC midnight.
 * When converted to local timezone, it can show the previous day.
 * 
 * Solution: Parse date strings manually and create dates with local timezone.
 */

/**
 * Parse a date string (YYYY-MM-DD or ISO format) and return a Date object in local timezone
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format
 * @returns {Date} - Date object in local timezone
 */
export function parseLocalDate(dateString) {
  if (!dateString) return null;
  
  // Handle ISO format (2025-01-20T00:00:00.000Z)
  const dateOnly = dateString.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  
  // Create date with local timezone (months are 0-indexed)
  return new Date(year, month - 1, day);
}

/**
 * Format a date string to pt-BR locale format (dd/mm/yyyy)
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format
 * @returns {string} - Formatted date string
 */
export function formatDateBR(dateString) {
  if (!dateString) return '-';
  
  const dateOnly = dateString.split('T')[0];
  const [year, month, day] = dateOnly.split('-');
  
  return `${day}/${month}/${year}`;
}

/**
 * Format a date string to short pt-BR format (dd/mm)
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format
 * @returns {string} - Formatted date string
 */
export function formatDateShortBR(dateString) {
  if (!dateString) return '-';
  
  const dateOnly = dateString.split('T')[0];
  const [, month, day] = dateOnly.split('-');
  
  return `${day}/${month}`;
}

/**
 * Format a date string to long pt-BR format (e.g., "20 de janeiro de 2025")
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format
 * @returns {string} - Formatted date string
 */
export function formatDateLongBR(dateString) {
  if (!dateString) return '-';
  
  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];
  
  const dateOnly = dateString.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  
  return `${day} de ${months[month - 1]} de ${year}`;
}

/**
 * Format a date string to full pt-BR format with weekday (e.g., "segunda-feira, 20 de janeiro de 2025")
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format
 * @returns {string} - Formatted date string
 */
export function formatDateFullBR(dateString) {
  if (!dateString) return '-';
  
  const weekdays = [
    'domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 
    'quinta-feira', 'sexta-feira', 'sábado'
  ];
  
  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];
  
  const date = parseLocalDate(dateString);
  if (!date) return '-';
  
  const weekday = weekdays[date.getDay()];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  return `${weekday}, ${day} de ${month} de ${year}`;
}

/**
 * Get today's date as YYYY-MM-DD string in local timezone
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export function getTodayString() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if a date string represents today
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format
 * @returns {boolean}
 */
export function isToday(dateString) {
  if (!dateString) return false;
  return dateString.split('T')[0] === getTodayString();
}

/**
 * Check if a date string represents yesterday
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format
 * @returns {boolean}
 */
export function isYesterday(dateString) {
  if (!dateString) return false;
  
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const year = yesterday.getFullYear();
  const month = String(yesterday.getMonth() + 1).padStart(2, '0');
  const day = String(yesterday.getDate()).padStart(2, '0');
  
  return dateString.split('T')[0] === `${year}-${month}-${day}`;
}
